import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, tap } from 'rxjs';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MatDialogRef } from '@angular/material/dialog';
import { Tag, TagType } from '~core/models/tag.models';
import { TagService } from '~app/app/~shared/services/tag.service';
import { AdvertiserSectorCreationDialogComponent } from '../../modals/advertiser-sector-creation-dialog/advertiser-sector-creation-dialog.component';

@Component({
  selector: 'advertiser-sector-creation-form',
  templateUrl: './advertiser-sector-creation-form.component.html',
  styleUrl: './advertiser-sector-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class AdvertiserSectorCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _tagService = inject(TagService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(
    MatDialogRef<AdvertiserSectorCreationDialogComponent>
  );

  sector = input<Tag>();

  groups$ = this._tagService.list({ type: TagType.GROUP, itemPerPage: 1000 });
  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  isSubmitting = signal(false);

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    }),
    parentId: new FormControl<string>('', {
      validators: [Validators.required]
    })
  });

  ngOnInit(): void {
    this.form.patchValue({
      name: this.sector()?.name,
      parentId: this.sector()?.parent?.id
    });
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const sectorId = this.sector()?.id;

    if (sectorId) {
      this.updateAdvertiserSector(sectorId);
    } else {
      this.createAdvertiserSector();
    }
  }

  createAdvertiserSector(): void {
    const formValue = this.form.value;

    this._tagService
      .create({
        name: formValue.name as string,
        type: TagType.SECTOR,
        parentId: formValue.parentId as string
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Secteur créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateAdvertiserSector(sectorId: string): void {
    const formValue = this.form.value;

    this._tagService
      .update(sectorId, {
        name: formValue.name as string,
        type: TagType.SECTOR,
        parentId: formValue.parentId as string
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Secteur modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }
}
